import { useMemo } from 'react'
import { useDataSource } from '../../../lib/dataSource'
import type { LifeSpace, Task } from '../../../lib/db/models'

/**
 * Scopes a cross-life-space task list to one life space. Orphan tasks (no
 * `projectId`) surface in their own lifespace via `task.lifeSpace`, mirroring
 * `filterUpcomingTasks` — paused Kurz-notiert entries from Work end up in
 * Work's lists, not Personal's.
 *
 * Shared by **Ruhend** (`useDormantTasks`), **Wiederkehrend**
 * (`useRecurringTasks`) and **Geschafft** (`useDoneTasks`); the list itself
 * comes from the data source. Returns `undefined` while loading; order is the
 * input's, callers sort.
 */
export function useLifeSpaceTasks(lifeSpace: LifeSpace, tasks: Task[] | undefined): Task[] | undefined {
  const projects = useDataSource().useLifeSpaceProjects(lifeSpace)

  const lifeSpaceProjectIds = useMemo(
    () => new Set((projects ?? []).map(p => p.id)),
    [projects],
  )

  return useMemo(() => {
    if (!tasks || !projects) return undefined
    return tasks.filter(task => {
      if (task.projectId == null) return task.lifeSpace === lifeSpace
      return lifeSpaceProjectIds.has(task.projectId)
    })
  }, [tasks, projects, lifeSpace, lifeSpaceProjectIds])
}
